import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck, Check, X, Pencil, Save } from 'lucide-react'
import { api } from '@/lib/api-client'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge, SeverityBadge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { formatCurrency } from '@roi365/shared'
import { toast } from 'sonner'
import { useState } from 'react'

interface ModerationItem {
  id: string
  recommendation_id: string
  tenant_id: string
  tenant_name: string
  type: string
  category: string
  title: string
  description: string
  severity: string
  priority: number
  confidence: number
  estimated_monthly_saving: number
  origin: string
  evidence_json: string
  recommended_action: string | null
  user_display_name: string | null
  user_principal_name: string | null
  created_at: string
}

const originLabels: Record<string, string> = { rule: 'Regla', ai_enriched: 'IA Enriquecida', ai_generated: 'IA Generada' }
const categoryLabels: Record<string, string> = {
  cost_optimization: 'Optimizacion costes',
  adoption: 'Adopcion',
  security: 'Seguridad',
  compliance: 'Cumplimiento',
}

export function AdminAiModerationPage() {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({ clientTitle: '', clientDescription: '', editNotes: '' })
  const [rejectNotes, setRejectNotes] = useState('')

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'ai', 'moderation'] })

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'ai', 'moderation'],
    queryFn: () => api.get<ModerationItem[]>('/admin/ai/moderation?perPage=100'),
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.put(`/admin/ai/moderation/${id}/approve`),
    onSuccess: () => { toast.success('Recomendacion aprobada y publicada'); invalidate() },
    onError: (e) => toast.error((e as Error).message),
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, editNotes }: { id: string; editNotes: string }) =>
      api.put(`/admin/ai/moderation/${id}/reject`, { editNotes }),
    onSuccess: () => { toast.success('Recomendacion rechazada'); invalidate(); setRejectingId(null); setRejectNotes('') },
    onError: (e) => toast.error((e as Error).message),
  })

  const editMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.put(`/admin/ai/moderation/${id}/edit`, data),
    onSuccess: () => { toast.success('Editada y publicada'); invalidate(); setEditingId(null) },
    onError: (e) => toast.error((e as Error).message),
  })

  const startEdit = (item: ModerationItem) => {
    setEditingId(item.id)
    setEditForm({ clientTitle: item.title, clientDescription: item.description, editNotes: '' })
  }

  const items = data ?? []

  return (
    <div className="space-y-6">
      <PageHeader title="Moderacion IA" description="Cola de recomendaciones pendientes de revision" />

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-32 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-200)' }} />)}</div>
      ) : items.length === 0 ? (
        <Card><EmptyState icon={ShieldCheck} title="Cola vacia" description="No hay recomendaciones pendientes de revision" /></Card>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const isEditing = editingId === item.id
            const isRejecting = rejectingId === item.id

            return (
              <Card key={item.id}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="premium">{item.tenant_name}</Badge>
                      <SeverityBadge severity={item.severity} />
                      <Badge variant={item.origin === 'rule' ? 'neutral' : item.origin === 'ai_enriched' ? 'info' : 'premium'}>
                        {originLabels[item.origin] ?? item.origin}
                      </Badge>
                      <Badge variant="neutral">{categoryLabels[item.category] ?? item.category}</Badge>
                      {item.confidence > 0 && (
                        <span className="text-xs font-medium" style={{ color: 'var(--color-text-tertiary)' }}>
                          Confianza: {Math.round(item.confidence * 100)}%
                        </span>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="mt-3 space-y-3">
                        <input
                          value={editForm.clientTitle}
                          onChange={(e) => setEditForm((f) => ({ ...f, clientTitle: e.target.value }))}
                          className="w-full rounded-md border px-3 py-2 text-sm font-semibold"
                          style={{ borderColor: 'var(--color-border)' }}
                        />
                        <textarea
                          value={editForm.clientDescription}
                          onChange={(e) => setEditForm((f) => ({ ...f, clientDescription: e.target.value }))}
                          rows={3}
                          className="w-full rounded-md border px-3 py-2 text-sm"
                          style={{ borderColor: 'var(--color-border)' }}
                        />
                        <textarea
                          value={editForm.editNotes}
                          onChange={(e) => setEditForm((f) => ({ ...f, editNotes: e.target.value }))}
                          rows={2}
                          placeholder="Notas de edicion (opcional)..."
                          className="w-full rounded-md border px-3 py-2 text-sm"
                          style={{ borderColor: 'var(--color-border)' }}
                        />
                      </div>
                    ) : (
                      <>
                        <h4 className="mt-2 font-semibold" style={{ color: 'var(--color-text-primary)' }}>{item.title}</h4>
                        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{item.description}</p>
                        {item.user_display_name && (
                          <p className="mt-1 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                            Usuario: {item.user_display_name} ({item.user_principal_name})
                          </p>
                        )}
                        {item.recommended_action && (
                          <div className="mt-2 rounded-md px-3 py-2 text-sm" style={{ backgroundColor: 'var(--color-gray-50)' }}>
                            <strong>Accion recomendada:</strong> {item.recommended_action}
                          </div>
                        )}
                      </>
                    )}

                    {isRejecting && (
                      <div className="mt-3">
                        <textarea
                          value={rejectNotes}
                          onChange={(e) => setRejectNotes(e.target.value)}
                          rows={2}
                          placeholder="Razon del rechazo (opcional)..."
                          className="w-full rounded-md border px-3 py-2 text-sm"
                          style={{ borderColor: 'var(--color-border)' }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--color-brand-accent-2)' }}>
                      {item.estimated_monthly_saving > 0 ? `${formatCurrency(item.estimated_monthly_saving)}/mes` : ''}
                    </p>
                    <div className="flex flex-wrap justify-end gap-1">
                      {isEditing ? (
                        <>
                          <Button variant="primary" size="sm" onClick={() => editMutation.mutate({ id: item.id, data: editForm })} loading={editMutation.isPending}>
                            <Save className="h-3.5 w-3.5" /> Guardar y publicar
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>Cancelar</Button>
                        </>
                      ) : isRejecting ? (
                        <>
                          <Button variant="danger" size="sm" onClick={() => rejectMutation.mutate({ id: item.id, editNotes: rejectNotes })} loading={rejectMutation.isPending}>
                            <X className="h-3.5 w-3.5" /> Confirmar rechazo
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setRejectingId(null); setRejectNotes('') }}>Cancelar</Button>
                        </>
                      ) : (
                        <>
                          <Button variant="primary" size="sm" onClick={() => approveMutation.mutate(item.id)} loading={approveMutation.isPending}>
                            <Check className="h-3.5 w-3.5" /> Aprobar
                          </Button>
                          <Button variant="secondary" size="sm" onClick={() => startEdit(item)}>
                            <Pencil className="h-3.5 w-3.5" /> Editar
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setRejectingId(item.id)}>
                            <X className="h-3.5 w-3.5" /> Rechazar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
